import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import { CreateAccountsSchema, UpdateAccountsSchema } from "./account.schema";

export const AccountIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const AccountBalanceHistoryInputSchema = z.object({
  businessId: z.string().trim().min(1),
  accountId: z.string().trim().min(1),
  period: z.enum(["monthly", "yearly"]),
});

export const ListAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  types: z.array(z.string().trim().min(1)).optional(),
  classIds: z.array(z.string().trim().min(1)).optional(),
});

export const ListAllAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  /** Optional projection: return only these fields per account (whitelisted server-side). */
  fields: z.array(z.string().trim().min(1)).optional(),
});

export const CreateAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateAccountsSchema,
});

export const UpdateAccountsInputSchema = z.object({
  ...AccountIdInputSchema.shape,
  updates: UpdateAccountsSchema,
});

export const BulkCreateAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  items: z.array(CreateAccountsSchema).min(1).max(100),
});

export const BulkDeleteAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(100),
});

export const listAccounts = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.list", async () => {
      const { listAccountsForUser } = await import("./account.server");
      return listAccountsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        types: data.types,
        classIds: data.classIds,
      });
    });
  });

export const listAllAccounts = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListAllAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.listAll", async () => {
      const { listAllAccountsForUser } = await import("./account.server");
      return listAllAccountsForUser(context.user.id, data.businessId, {
        fields: data.fields,
      });
    });
  });

export const createAccounts = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.create", async () => {
      const { createAccountsForUser } = await import("./account.server");
      return createAccountsForUser(context.user.id, data.businessId, data.item);
    });
  });

export const updateAccounts = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.update", async () => {
      const { updateAccountsForUser } = await import("./account.server");
      const result = await updateAccountsForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteAccounts = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => AccountIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.delete", async () => {
      const { deleteAccountsForUser } = await import("./account.server");
      const result = await deleteAccountsForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const getAccount = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => AccountIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.get", async () => {
      const { getAccountForUser } = await import("./account.server");
      const result = await getAccountForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const bulkCreateAccounts = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BulkCreateAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.bulkCreate", async () => {
      const { bulkCreateAccountsForUser } = await import("./account.server");
      return bulkCreateAccountsForUser(
        context.user.id,
        data.businessId,
        data.items,
      );
    });
  });

export const bulkDeleteAccounts = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BulkDeleteAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.bulkDelete", async () => {
      const { bulkDeleteAccountsForUser } = await import("./account.server");
      return bulkDeleteAccountsForUser(
        context.user.id,
        data.businessId,
        data.ids,
      );
    });
  });

export const getAccountBalanceHistory = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => AccountBalanceHistoryInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accounts.balanceHistory", async () => {
      const { getAccountBalanceHistoryForUser } =
        await import("./account.server");
      return getAccountBalanceHistoryForUser(
        context.user.id,
        data.businessId,
        data.accountId,
        data.period,
      );
    });
  });
